import { apiClient } from '@api/client';
import { toArray } from '@/shared/lib/toArray';

export interface Customer {
  // ⚠️ ЕДИНСТВЕННОЕ объявление ответа списка. features/customers/types реэкспортирует
  // отсюда: раньше там лежал ВТОРОЙ одноимённый Customer (22 поля против 42), таблица
  // импортировала этот, и правки «не того» файла молча не покрывали типы.
  //
  // ⚠️ Убраны 4 поля, которых сервер НЕ шлёт: date_activity_inet и group_id — остатки
  // переименований (стали date_last_activity_internet и groups), new_tariff_date и
  // new_tariff_profit — доп. поля эталона, в V5 не портированные. Объявленное, но не
  // приходящее поле читается как «данные есть, просто пусто».
  id: number;
  username: string;
  name: string;
  email: string;
  phone?: string;
  balance: number;
  tariff_id: number;
  tariff_name?: string;
  status_id: number;
  status_name?: string;
  date_connect: number;
  date_last_activity?: number;
  apartment?: string;
  ip?: string;
  mac?: string;
  is_online?: boolean;
  date_last_activity_net?: number;
  // Дополнительные поля, приходящие из backend (необязательные)
  comment?: string;
  date_add?: number;
  address?: string;
  agreement_number?: string;
  entrance_number?: number;
  level_number?: string;
  discount?: number;
  account_number?: string;
  credit?: number;
  manager?: string;
  commutation?: string;
  additional_paid?: string;
  new_tariff?: string;
  level_full?: string;
  date_to_ex?: string;
  building_id?: number;
  /** Трафик в БАЙТАХ — та же единица, что у фильтра `traffic_in_min`. */
  traffic_in?: number | null;
  traffic_out?: number | null;
  // Колонки, закрытые в лупе 2026-09-05/06. ⚠️ Объявлять их нужно ЗДЕСЬ: таблица
  // импортирует Customer из этого файла, а не из features/customers/types.
  groups?: string;
  date_last_activity_internet?: number;
  level_bad?: boolean;
  /** Серверная проекция V4 PINGENABLE: карточка ping не должна знать system.settings. */
  ping_card_enabled?: boolean;
}

/**
 * PaginationParams - параметры для пагинации и фильтрации списка клиентов
 * 
 * Полная поддержка всех 64 Legacy фильтров из customer_list (FilterHtml.php)
 * Параметры передаются через query string в GET /api/v1/customers
 * 
 * Группы фильтров:
 *   - Базовые (26): статус, тариф, биллинг, группа, менеджер, здание, баланс, даты, IP/MAC, трафик
 *   - Приоритет 1 (12): группа тарифов, договоры, адреса, финансовые даты
 *   - Специализированные (10): услуги, задания, устройства, коммутация, реклама, ТМЦ
 *   - Редко используемые (16): тип здания, собственник, кабельная инфраструктура, полигоны
 */
export interface PaginationParams {
  page?: number;
  limit?: number;
  query?: string;
  sort_by?: string;
  sort_desc?: boolean;

  // Фильтры (согласно Legacy FilterHtml)
  status_id?: number;
  tariff_id?: number;
  billing_id?: number;
  group_id?: number;
  manager_id?: number;
  building_id?: number;
  apartment?: string;
  is_corporate?: boolean;
  is_child?: boolean;

  // Баланс (F_BALANCE)
  balance?: number;
  balance_operator?: 'eq' | 'lte' | 'gte' | 'lt' | 'gt' | 'ne';
  balance_min?: number;
  balance_max?: number;

  // Баланс + Кредит (F_BALANCE_CREDIT)
  balance_credit?: number;
  balance_credit_operator?: 'eq' | 'lte' | 'gte' | 'lt' | 'gt' | 'ne';
  balance_credit_min?: number;
  balance_credit_max?: number;

  // Даты (F_DATE_CONNECT)
  date_connect_from?: string;
  date_connect_to?: string;
  /** Эталонные операторы 1/2: '1' — дата известна, '0' — не заполнена (FilterHtml.php:1457,1466). */
  date_connect_known?: string;
  /** Оператор эталона: '1' — дата активности известна, '0' — неизвестна. */
  date_activity_known?: string;

  // Даты активности (F_DATE_ACTIVITY, F_DATE_ACTIVITY_INET)
  date_activity_from?: string;
  date_activity_to?: string;

  // IP/MAC (F_CUSTOMER_IPMAC_ADDITIONAL)
  has_ip?: boolean;
  has_mac?: boolean;

  // Теги (F_CUSTOMER_MARK)
  tag_ids?: number[];
  // Отрицание метки. В v4 кодировалось знаком минус в том же параметре
  // (CustomerListHtml.php:2942-2954); на проводе V5 названо явно.
  tag_ids_exclude?: number[];

  // Дополнительные фильтры (Legacy)
  customer_name?: string;       // F_CUSTOMER_NAME
  username?: string;             // F_CUSTOMER_LOGIN (поддержка запятой)
  customer_ids?: number[];       // F_CUSTOMER_ID

  // Фильтры actual_status (F_EX_CUSTOMER, F_CUSTOMER_POTENTIAL)
  include_ex?: boolean;          // Включать архивных
  include_potential?: boolean;   // Включать потенциальных
  only_ex?: boolean;             // Только архивные
  only_potential?: boolean;      // Только потенциальные

  // Трафик (F_TRAFIC_RX, F_TRAFIC_TX)
  traffic_in_min?: number;
  traffic_out_min?: number;

  // === ФАЗА 3: ПРИОРИТЕТ 1 ===
  tariff_group_id?: number;
  date_add_from?: string;
  date_add_to?: string;
  agreement_date?: string;
  agreement_number?: string;
  account_number?: string;
  entrance_number?: number;
  no_address?: boolean;
  has_discount?: boolean;
  date_last_plus_from?: string;
  date_last_plus_to?: string;
  date_balance_increase_from?: string;
  date_balance_increase_to?: string;
  date_balance_decrease_from?: string;
  date_balance_decrease_to?: string;
  bad_balance_period_from?: number;
  bad_balance_period_to?: number;
  date_to_ex_from?: string;
  date_to_ex_to?: string;
  date_to_disconnect_from?: string;
  date_to_disconnect_to?: string;

  // === ФАЗА 4: СПЕЦИАЛИЗИРОВАННЫЕ ===
  additional_service_ids?: number[];
  no_tasks?: boolean;
  has_unfinished_tasks?: boolean;
  device_ids?: number[];
  node_ids?: number[];
  double_connect?: boolean;
  not_connected?: boolean;
  foreign_building?: boolean;
  device_down?: boolean;
  advertisement_ids?: number[];
  with_inventory?: number;
  overlap_address?: boolean;

  // === ФАЗА 5: РЕДКО ИСПОЛЬЗУЕМЫЕ ===
  building_type_id?: number;
  owner_id?: number;
  date_potential_add_exists?: boolean;
  credit_only?: boolean;
  cable_line_ids?: number[];
  cable_wire_ids?: number[];
  splitter_ids?: number[];
  cross_ids?: number[];
  polygon_id?: number;
  date_to_drain_from?: string;
  date_to_drain_to?: string;
}

export interface PaginationResponse {
  page: number;
  limit: number;
  total: number;
  total_pages: number;
}

export interface CustomersResponse {
  items: Customer[];
  pagination: PaginationResponse;
}

export interface BalanceOperation {
  id: number;
  amount: number;
  balance_before: number;
  balance_after: number;
  comment: string;
  created_at: number;
}

export interface BalanceHistoryResponse {
  items: BalanceOperation[];
  pagination: PaginationResponse;
}

export interface TariffListItem {
  id: number;
  billing_id: number;
  name: string;
  price: number;
  speed_tx: number;
  speed_rx: number;
  traffic_include: number;
  description: string;
  service_type: string;
  is_active: boolean;
  /**
   * Имя биллинга, которому принадлежит тариф. Объявление отставало от провода: поле
   * приходит (снято с `GET /api/v1/tariffs` 2026-09-03, вместе с `rate`, `rate_full`,
   * `payment_interval`, `can_manage`, `manage_block_reason`), но в типе его не было.
   * Помечено необязательным: остальные потребители на него не рассчитывают, и объявлять
   * обязательным то, что не проверено на всех путях чтения, значит повторить ту же ошибку
   * в другую сторону.
   */
  billing_name?: string;
}

export interface TariffListResponse {
  items: TariffListItem[];
  total: number;
}

export interface CustomerTransaction {
  Typer: string;
  DateDo: string;
  employee_id: number;
  Amount: number;
  Comment: string;
}

export interface CustomerTransactionsPage {
  items: CustomerTransaction[];
  total: number;
}

export interface CustomerTransactionsParams {
  sort_desc?: boolean;
  limit?: number;
  offset?: number;
}

export interface CustomerChangesHistoryItem {
  occurred_at: string;
  action_type_id: number;
  employee_id: number | null;
  employee_name: string;
  object_name: string;
  comment: string;
}

export interface CustomerChangesHistoryParams {
  date_from: string;
  date_to: string;
  page: number;
  limit: number;
}

export interface CustomerChangesHistoryResponse {
  items: CustomerChangesHistoryItem[];
  total: number;
  page: number;
  limit: number;
}

export interface CustomerInventoryMovementItem {
  operation_id: number;
  occurred_at: string;
  employee_id: number | null;
  employee_name: string;
}

export interface CustomerInventoryMovementsParams {
  date_from: string;
  date_to: string;
  page: number;
  limit: number;
}

export interface CustomerInventoryMovementsResponse {
  items: CustomerInventoryMovementItem[];
  total: number;
  page: number;
  limit: number;
}

export interface CustomerCommutationHistoryItem {
  log_id: number;
  occurred_at: string;
  is_deleted: boolean;
  employee_id: number | null;
  employee_name: string;
  comment: string | null;
}

export interface CustomerCommutationHistoryParams {
  date_from: string;
  date_to: string;
  page: number;
  limit: number;
}

export interface CustomerCommutationHistoryResponse {
  items: CustomerCommutationHistoryItem[];
  total: number;
  page: number;
  limit: number;
}

export interface CustomerDirectDeviceItem {
  id: number;
  customerPort?: number | null;
  peerDeviceId: number;
  peerDeviceName: string;
  peerPort?: number | null;
  peerFastLinks?: Array<{ label: string; url: string }>;
}

export interface CustomerDirectCommutationsResponse {
  items: CustomerDirectDeviceItem[];
  total: number;
  page: number;
  limit: number;
}

export type CustomerHistoryFeedSourceType = 'financial' | 'change' | 'inventory' | 'commutation';

export interface CustomerHistoryFeedItem {
  occurred_at: string;
  source_type: CustomerHistoryFeedSourceType;
  reference_id: number;
  description: string;
  amount?: number;
  employee_id?: number;
  employee_name: string;
  comment: string;
}

export interface CustomerHistoryFeedParams {
  date_from: string;
  date_to: string;
  page: number;
  limit: number;
}

export interface CustomerHistoryFeedResponse {
  items: CustomerHistoryFeedItem[];
  total: number;
  page: number;
  limit: number;
  billing_unavailable: boolean;
}

export interface CustomerMessage {
  id: number;
  customer_id: number;
  body: string;
  answer: string;
  date_add: string;
  employee_id?: number | null;
  is_archive: boolean;
}

export interface CustomerMessagesResponse {
  items: CustomerMessage[];
  total: number;
  page: number;
  limit: number;
  total_pages: number;
}

// ========================================
// Customer Statistics (Real Data)
// ========================================

export interface CustomerStats {
  total: number;
  active: number;
  blocked: number;
  paused: number;

  balance: {
    total: number;
    average: number;
    max: number;
    min: number;
    positive_count: number;
    zero_count: number;
    negative_count: number;
    debt?: number;
    credit?: number;
  };

  activity: {
    last_15min: number;
    last_hour: number;
    today: number;
    inactive?: number;
  };

  notifications: {
    low_balance_count: number;
    low_balance_threshold: number;
  };

  revenue?: {
    top_tariff?: {
      tariff_id: number;
      tariff_name: string;
      count: number;
      price: number;
    };
    avg_tariff_price?: number;
    total_monthly_revenue?: number;
  };

  growth?: {
    weekly_new?: number;
    monthly_new?: number;
    churn?: number;
  };

  internet?: {
    online_count?: number;
    offline_count?: number;
    no_ip_count?: number;
    no_mac_count?: number;
  };

  devices?: {
    total?: number;
    online?: number;
    offline?: number;
    no_device?: number;
    double_connect?: number;
    problematic_onu?: number;
  };
}

export interface CreateCustomerRequest {
  username: string;
  name: string;
  email?: string;
  tariff_id: number;
}

export interface UpdateCustomerRequest {
  name?: string;
  email?: string;
  username?: string;
  building_id?: number;
  apartment?: string;
  comment?: string;
  status_id?: number;
}

export interface ChangeBalanceRequest {
  amount: number;
  comment: string;
}

// Customers API
export async function getCustomers(params: PaginationParams = {}): Promise<CustomersResponse> {
  const response = await apiClient.get('/customers', { params });
  return response.data;
}

// Get Customer Statistics (REAL DATA from database aggregation)
/**
 * Выгружает список абонентов файлом ПО ТЕКУЩИМ ФИЛЬТРАМ.
 *
 * Параметры — те же, что у списка: сервер разбирает их одной функцией
 * (`parseCustomerSearchFilter`), поэтому файл не может разойтись с экраном. Прежний
 * компонент-сирота `CustomerExport.tsx` брал первые 1000 абонентов БЕЗ фильтров, молча
 * обрезал на этом числе и выдумывал подпись статуса — он удалён.
 *
 * Ответ читается как Blob: это файл, а не JSON.
 */
/**
 * Параметры выгрузки — те же, что у списка, плюс СОСТАВ КОЛОНОК.
 *
 * `columns` — ключи через запятую, в порядке экрана. Сервер отдаёт ровно запрошенное и
 * отказывает с именем неизвестного ключа; молча выбросить колонку он не может.
 */
export interface ExportCustomersParams extends PaginationParams {
  columns?: string;
}

export async function exportCustomers(params: ExportCustomersParams = {}): Promise<Blob> {
  const response = await apiClient.get('/customers/export', {
    params: { ...params, format: 'csv' },
    responseType: 'blob',
  });
  return response.data as Blob;
}

export async function getCustomerStats(): Promise<CustomerStats> {
  const response = await apiClient.get('/customers/stats');
  return response.data;
}

export async function getCustomer(id: number): Promise<Customer> {
  const response = await apiClient.get(`/customers/${id}`);
  return response.data;
}

export async function createCustomer(data: CreateCustomerRequest): Promise<Customer> {
  const response = await apiClient.post('/customers', data);
  return response.data;
}

export async function updateCustomer(id: number, data: UpdateCustomerRequest): Promise<Customer> {
  const response = await apiClient.put(`/customers/${id}`, data);
  return response.data;
}

// CustomerDeleteBlocker — один объект, блокирующий удаление (Legacy: CustomerAR::checkDelete,
// каждое основание с typeName/name/url — «покажи КАКОЙ объект блокирует», не просто счётчик).
export interface CustomerDeleteBlocker {
  typeName: string;
  name: string;
  url?: string;
}

export interface CustomerDeleteCheck {
  canDelete: boolean;
  blockers: CustomerDeleteBlocker[];
}

// checkCustomerDeleteBlockers — основания отказа ДО подтверждения оператором, тот же список,
// что пересчитает DELETE внутри транзакции при подтверждении.
export async function checkCustomerDeleteBlockers(id: number): Promise<CustomerDeleteCheck> {
  const response = await apiClient.get<CustomerDeleteCheck>(`/customers/${id}/delete-check`);
  return response.data;
}

export async function deleteCustomer(id: number): Promise<void> {
  await apiClient.delete(`/customers/${id}`);
}

export async function changeBalance(id: number, data: ChangeBalanceRequest): Promise<Customer> {
  const response = await apiClient.post(`/customers/${id}/balance`, data);
  return response.data;
}

export async function getBalanceHistory(id: number, params: PaginationParams = {}): Promise<BalanceHistoryResponse> {
  const response = await apiClient.get(`/customers/${id}/balance/history`, { params });
  return response.data;
}

export async function getCustomerTransactionsPage(
  id: number,
  params: CustomerTransactionsParams,
): Promise<CustomerTransactionsPage> {
  const response = await apiClient.get(`/customers/${id}/transactions`, {
    params: {
      sort_desc: params.sort_desc ?? undefined,
      limit: params.limit,
      offset: params.offset,
    },
  });
  const rawTotalHeader = response.headers?.['x-total-count'];
  const items: CustomerTransaction[] = response.data;
  const normalizedTotal = typeof rawTotalHeader === 'string' ? rawTotalHeader.trim() : '';
  const total = /^\d+$/.test(normalizedTotal) ? Number(normalizedTotal) : Number.NaN;

  if (!Number.isSafeInteger(total) || total < 0) {
    throw new Error('Invalid X-Total-Count header');
  }

  return {
    items,
    total,
  };
}

export async function getCustomerChangesHistory(
  id: number,
  params: CustomerChangesHistoryParams,
): Promise<CustomerChangesHistoryResponse> {
  const response = await apiClient.get<CustomerChangesHistoryResponse>(
    `/customers/${id}/changes-history`,
    { params },
  );
  return response.data;
}

export async function getCustomerInventoryMovements(
  id: number,
  params: CustomerInventoryMovementsParams,
): Promise<CustomerInventoryMovementsResponse> {
  const response = await apiClient.get<CustomerInventoryMovementsResponse>(
    `/customers/${id}/inventory-movements`,
    { params },
  );
  return response.data;
}

export async function getCustomerCommutationHistory(
  id: number,
  params: CustomerCommutationHistoryParams,
): Promise<CustomerCommutationHistoryResponse> {
  const response = await apiClient.get<CustomerCommutationHistoryResponse>(
    `/customers/${id}/commutation-history`,
    { params },
  );
  return response.data;
}

export async function getCustomerDirectCommutations(id: number, page = 1): Promise<CustomerDirectCommutationsResponse> {
  const response = await apiClient.get<CustomerDirectCommutationsResponse>(
    `/customers/${id}/commutations/direct`,
    { params: { page, limit: 20 } },
  );
  return response.data;
}

export async function getCustomerHistoryFeed(
  id: number,
  params: CustomerHistoryFeedParams,
): Promise<CustomerHistoryFeedResponse> {
  const response = await apiClient.get<CustomerHistoryFeedResponse>(
    `/customers/${id}/history-feed`,
    { params },
  );
  return response.data;
}

export async function getCustomerDevices(customerId: number) {
  const response = await apiClient.get(`/customers/${customerId}/devices`);
  return response.data;
}

export async function getServices() {
  const response = await apiClient.get('/services');
  return response.data;
}

export async function getTariffs(params?: {
  billing_id?: number;
  name?: string;
  service_type?: string;
  traffic_category?: 'lim' | 'unlim';
  limit?: number;
  offset?: number;
}): Promise<TariffListResponse> {
  const response = await apiClient.get<TariffListResponse>('/tariffs', { params });
  return response.data;
}

export async function getCustomerServices(customerId: number) {
  const response = await apiClient.get(`/customers/${customerId}/services`);
  return response.data;
}

// Phones API
export async function getCustomerPhones(customerId: number) {
  const response = await apiClient.get(`/customers/${customerId}/phones`);
  return response.data;
}

export async function createCustomerPhone(
  customerId: number,
  data: { phone: string; phone_type: string; comment?: string }
) {
  const response = await apiClient.post(`/customers/${customerId}/phones`, data);
  return response.data;
}

export async function updateCustomerPhone(
  customerId: number,
  phone: string,
  data: { phone?: string; phone_type?: string; comment?: string; is_main?: boolean }
) {
  const response = await apiClient.put(
    `/customers/${customerId}/phones/${encodeURIComponent(phone)}`,
    data
  );
  return response.data;
}

export async function deleteCustomerPhone(customerId: number, phone: string) {
  const response = await apiClient.delete(
    `/customers/${customerId}/phones/${encodeURIComponent(phone)}`
  );
  return response.data;
}

// Groups API
export async function getCustomerGroups() {
  const response = await apiClient.get('/customer-groups');
  return response.data;
}

export async function createCustomerGroup(data: {
  name: string;
  color?: string;
  comment?: string;
}) {
  const response = await apiClient.post('/customer-groups', data);
  return response.data;
}

export async function updateCustomerGroup(
  id: number,
  data: { name?: string; color?: string; comment?: string }
) {
  const response = await apiClient.put(`/customer-groups/${id}`, data);
  return response.data;
}

export async function deleteCustomerGroup(id: number) {
  const response = await apiClient.delete(`/customer-groups/${id}`);
  return response.data;
}

// IP Management API
export async function getCustomerIPs(customerId: number) {
  const response = await apiClient.get(`/customers/${customerId}/ips`);
  return response.data;
}

export async function createCustomerIP(
  customerId: number,
  data: {
    ip_start: string;
    ip_end: string;
    subnet: number;
    comment?: string;
    color?: string;
  }
) {
  const response = await apiClient.post(`/customers/${customerId}/ips`, data);
  return response.data;
}

export async function updateCustomerIP(
  customerId: number,
  subnet: number,
  data: {
    ip_start?: string;
    ip_end?: string;
    subnet?: number;
    comment?: string;
    color?: string;
  }
) {
  const response = await apiClient.put(`/customers/${customerId}/ips/${subnet}`, data);
  return response.data;
}

export async function deleteCustomerIP(customerId: number, subnet: number) {
  const response = await apiClient.delete(`/customers/${customerId}/ips/${subnet}`);
  return response.data;
}

// Смена тарифа клиента (Legacy: tarifChange)
export async function changeTariff(customerId: number, tariffId: number) {
  const response = await apiClient.put(`/customers/${customerId}/tariff`, {
    tariff_id: tariffId,
  });
  return response.data;
}

// Смена статуса клиента (Legacy: stateChange)
export async function changeStatus(customerId: number, statusId: number) {
  const response = await apiClient.put(`/customers/${customerId}/status`, {
    status_id: statusId,
  });
  return response.data;
}

// Смена пароля клиента (Legacy: passChange)
export async function changePassword(
  customerId: number,
  oldPassword: string,
  newPassword: string
) {
  const response = await apiClient.put(`/customers/${customerId}/password`, {
    old_password: oldPassword,
    new_password: newPassword,
  });
  return response.data;
}

// Получить сообщения клиента (Legacy: msg)
export async function getCustomerMessages(
  customerId: number,
  page = 1,
  limit = 20,
): Promise<CustomerMessagesResponse> {
  const response = await apiClient.get(`/customers/${customerId}/messages`, {
    params: { page, limit },
  });
  return response.data;
}

// Получить все теги системы (общий справочник, делится с разделом оборудования)
export async function getAllTags() {
  const response = await apiClient.get('/tags');
  return response.data;
}

// Получить метки, разрешённые абонентам, — источник выпадашки карточки.
// Не весь справочник: в v4 выпадашка берёт только категорию customer из erp.tags_object_types,
// иначе оператору предлагают метки узлов, кабельных линий и устройств.
export async function getCustomerAvailableTags() {
  const response = await apiClient.get('/tags/customer');
  return response.data;
}

/** Метка в раскладке — те же поля оформления, что у чипа в карточке. */
export interface TagBreakdownTag {
  id: number;
  name: string;
  color?: string;
  text_color?: string;
}

/** Строка раскладки: метка и два числа. */
export interface TagBreakdownRow {
  tag: TagBreakdownTag;
  with_count: number;
  without_count: number;
}

/**
 * Раскладка абонентов по меткам.
 *
 * customer_count — знаменатель: два числа строки обязаны давать его в сумме. Он приходит с
 * сервера, а не считается на фронте: множество абонентов задаётся адресным доступом
 * оператора и известно только серверу.
 */
export interface TagsBreakdownResponse {
  customer_count: number;
  rows: TagBreakdownRow[];
}

// Раскладка «с меткой / без метки» — экран пункта меню «Метки».
// Право — customers.mark.read (в v4 право 33), гейт стоит и на роуте, и на пункте меню.
export async function getCustomerTagsBreakdown(): Promise<TagsBreakdownResponse> {
  const response = await apiClient.get('/tags/customer-breakdown');
  return response.data.data;
}

// Получить теги клиента
export async function getCustomerTags(customerId: number) {
  const response = await apiClient.get(`/customers/${customerId}/tags`);
  return response.data;
}

// Добавить тег клиенту (Legacy: markAdd)
export async function addCustomerTag(customerId: number, tagId: number) {
  const response = await apiClient.post(`/customers/${customerId}/tags`, {
    tag_id: tagId,
  });
  return response.data;
}

// Удалить тег от клиента (Legacy: markDelete)
export async function removeCustomerTag(customerId: number, tagId: number) {
  const response = await apiClient.delete(`/customers/${customerId}/tags/${tagId}`);
  return response.data;
}

/** Рекламная кампания на карточке абонента (эталон CustomerHtml::advertSave/advertDelete). */
export interface CustomerAdvertisement {
  id: number;
  name: string;
  date_start: string;
  date_stop: string;
}

// Получить кампании, привязанные к абоненту
export async function getCustomerAdvertisements(customerId: number) {
  const response = await apiClient.get(`/customers/${customerId}/advertisements`);
  return response.data;
}

// Получить действующие кампании, ещё не привязанные к абоненту — источник выпадашки «+ назначить»
export async function getAvailableAdvertisementsForCustomer(customerId: number) {
  const response = await apiClient.get(`/customers/${customerId}/advertisements/available`);
  return response.data;
}

// Привязать кампанию к абоненту (Legacy: advertSave)
export async function attachCustomerAdvertisement(customerId: number, advertisementId: number) {
  const response = await apiClient.post(`/customers/${customerId}/advertisements`, {
    advertisement_id: advertisementId,
  });
  return response.data;
}

// Отвязать кампанию от абонента (Legacy: advertDelete)
export async function detachCustomerAdvertisement(customerId: number, advertisementId: number) {
  const response = await apiClient.delete(`/customers/${customerId}/advertisements/${advertisementId}`);
  return response.data;
}

/** ONU абонента на карточке клиента (эталон CustomerHtml::collectCustomerOnuDevicesWithPollData). */
export interface CustomerOnuDevice {
  id: number;
  device_id: number;
  identifier: string;
  sn: string | null;
  mac: string | null;
  vendor: string | null;
  model: string | null;
  description: string | null;
  iface_name: string | null;
  distance: number | null;
  level_onu_rx: number | null;
  level_olt_rx: number | null;
  is_online: boolean;
  level_status: string;
  owner_device_name: string | null;
  updated_at: string;
}

// Получить ONU абонента (across all OLT) с текущим уровнем сигнала
export async function getCustomerOnuDevices(customerId: number) {
  const response = await apiClient.get(`/customers/${customerId}/onu-devices`);
  return response.data;
}

// Метки НЕ заводятся из карточки абонента: создание метки — запись в справочник, и в v4 она
// закрыта правом настроек (RIGHT_SETTING = 6), а не абонентским. Клиент справочника —
// features/settings/api/objectMarksApi.ts (POST /settings/map/object-marks, settings.map.manage).

// Получить историю MAC адресов клиента (Legacy: getMacHistory)
export async function getCustomerMacHistory(customerId: number) {
  const response = await apiClient.get(`/customers/${customerId}/mac-history`);
  return response.data;
}

// ========================================
// Расширенный поиск и архивирование
// ========================================

// Расширенный поиск клиентов (Legacy: getCustomersId)
// Расширенный поиск абонентов клиентом БОЛЬШЕ НЕ ВЫЗЫВАЕТСЯ.
//
// `POST /customers/search` не искала по критериям: строила SQL-условия, писала их в журнал и
// выбрасывала, после чего возвращала первые `limit` абонентов по id. Замер 2026-09-03:
// несуществующее имя, «Иванов», `state_id=2` и `building_id=999999` дали один и тот же
// ответ — 1000 id, начиная с 15, 17, 19. Ручка теперь честно отвечает 501, а модалка
// применяет критерии к фильтру списка (`lib/advancedSearchToFilters.ts`).
//
// Клиентская обёртка удалена намеренно: оставленная без потребителей, она была бы
// приглашением подключить её обратно.

// Архивировать клиента (Legacy: toEx)
export async function archiveCustomer(customerId: number) {
  const response = await apiClient.put(`/customers/${customerId}/archive`);
  return response.data;
}

// Восстановить клиента из архива (Legacy: toNormal)
export async function restoreCustomer(customerId: number) {
  const response = await apiClient.put(`/customers/${customerId}/restore`);
  return response.data;
}

// ========================================
// Utility functions
// ========================================

// Поиск клиента по полю (Legacy: getAbonId)
export async function getCustomerByField(params: {
  data_typer: 'login' | 'ip' | 'mac' | 'phone' | 'account' | 'email' | 'codeti' | 'billing_uid' | 'agreement_number';
  data_value: string;
  billing_id?: number;
  is_skip_old?: boolean;
}) {
  const response = await apiClient.post('/customers/by-field', params);
  return response.data;
}

// ========================================
// Активность абонентов (Sessions & Online)
// ========================================

/**
 * Источник метрики активности. Это РАЗНЫЕ измерения, а не дубль:
 * «в сети» приходит от сетевого оборудования, «в интернете» — от биллинга.
 */
export type ActivitySource = 'net' | 'internet';

export interface CustomerActivityItem {
  customer_id: number;
  customer_name: string;
  username: string;
  balance: number;
  /**
   * Есть ли у абонента источник выбранной метрики. Состояний ТРИ:
   *   has_source=false            — «нет данных» (ручной биллинг, нет billing_id,
   *                                 выключен флаг обновления активности);
   *   has_source=true, is_online  — онлайн;
   *   has_source=true, !is_online — офлайн.
   * Выводить «нет источника» из пустой метки нельзя: это чтение «не знаем» как «нет».
   */
  has_source: boolean;
  is_online: boolean;
  last_activity_at: string; // ISO 8601
  building?: string;
  apartment?: string;
}

export interface ActivityStats {
  total_customers: number;
  online_count: number;
  offline_count: number;
  /** Инвариант: online_count + offline_count + no_source_count === total_customers */
  no_source_count: number;
  last_hour_count: number;
  last_day_count: number;
}

export interface GetCustomersActivityResponse {
  /** Источник, по которому сервер фактически посчитал ответ. */
  source: ActivitySource;
  items: CustomerActivityItem[];
  total: number;
  page: number;
  limit: number;
  total_pages: number;
  stats: ActivityStats;
}

export interface GetCustomersActivityParams {
  source?: ActivitySource;
  query?: string;
  is_online?: boolean;
  status_id?: number;
  building_id?: number;
  date_from?: number; // Unix timestamp
  date_to?: number; // Unix timestamp
  page?: number;
  limit?: number;
  sort_by?: 'last_activity' | 'customer_name' | 'traffic';
  sort_desc?: boolean;
}

export async function getCustomersActivity(
  params: GetCustomersActivityParams = {}
): Promise<GetCustomersActivityResponse> {
  const response = await apiClient.get('/customers/activity', { params });
  return response.data;
}

/**
 * Доступность источников метрики активности на уровне ИНСТАЛЛЯЦИИ.
 * По ней гейтится пункт меню «Активность в интернете» — один запрос в кеше
 * react-query на сессию, а не запрос на каждый рендер сайдбара.
 */
export interface ActivitySourcesResponse {
  settings: {
    hasInternetActivitySource: boolean;
  };
}

export async function getActivitySources(): Promise<ActivitySourcesResponse> {
  const response = await apiClient.get('/customers/activity/sources');
  return response.data;
}

/**
 * График активности абонентов (день/месяц/год, сеть vs интернет) — эталон
 * CustomerChartActivityHtml.php. Гейт: customers.analytics.activity + ADDRESS_ALL
 * (403 без адресного write-scope целиком, отчёт системный, сузить нечем).
 */
export interface ActivityChartPoint {
  label: string;
  net: number;
  internet: number;
}

export interface ActivityChartSeries {
  day: ActivityChartPoint[];
  month: ActivityChartPoint[];
  year: ActivityChartPoint[];
  /**
   * Время самого свежего снимка в таблицах-источниках (unix), 0 — снимков нет вовсе.
   * Экран РАЗЛИЧАЕТ по нему две причины пустого графика: сборщик ни разу не отработал
   * против «работал и перестал». Поле может отсутствовать, если бэкенд старше правки, —
   * тогда экран говорит только то, что знает точно.
   */
  last_snapshot_at?: number;
}

export async function getActivityChart(): Promise<ActivityChartSeries> {
  const response = await apiClient.get('/customers/activity/chart');
  return response.data;
}

// --- Отчёт роста абонентов по дереву адресов (growth-charts.2) ---
// Эталон CustomerChartIncreaseHtml.php. Дерево адресов × периоды, в ячейке три числа.

export interface GrowthPeriod {
  key: string;
  /** Служебный бакет «дата подключения не заполнена»: подписывается словами, не рисуется на графике. */
  is_unknown: boolean;
  year: number;
  month: number;
  /**
   * Границы периода в unix-секундах, посчитанные Postgres в поясе сессии. Только ими
   * строится drill-down по оттоку (`date_disconnect` — integer): считать их на клиенте
   * значит разъехаться с отчётом на смещение пояса. У служебного бакета обе нулевые.
   */
  from_unix: number;
  to_unix: number;
}

export interface GrowthCell {
  connect: number;
  disconnect: number;
  /** null = ячейка печатается пустой (воспроизведённое поведение v4, см. бэкенд). */
  total: number | null;
}

export interface GrowthRow {
  id: number;
  parent_id: number | null;
  name: string;
  cells: Record<string, GrowthCell>;
}

export interface GrowthChartPoint {
  period: string;
  connect: number;
  disconnect: number;
  total: number;
}

export interface GrowthBillingOption {
  id: number;
  name: string;
  count: number;
}

export interface CustomerGrowthReport {
  mode: 'year' | 'month';
  year: number;
  years: number[];
  periods: GrowthPeriod[];
  rows: GrowthRow[];
  total: GrowthRow;
  chart: GrowthChartPoint[];
  /** Биллинги, встречающиеся в доступной оператору выборке; id = 0 — без биллинга. */
  billings: GrowthBillingOption[];
}

export interface GrowthReportParams {
  /** Год → режим месяцев; без него — режим годов. */
  year?: number;
  /** 0 все, 1 физлица, 2 юрлица. */
  customer_type?: number;
  /** 0 все, -1 без биллинга, иначе конкретный биллинг. */
  billing_id?: number;
}

export async function getGrowthReport(
  params: GrowthReportParams = {},
): Promise<CustomerGrowthReport> {
  const response = await apiClient.get('/customers/charts/growth', { params });
  return response.data;
}

// ✅ Новый endpoint для получения агрегированной статистики по должникам
// Рассчитывается на backend в одном SQL запросе для оптимальности на больших сетях (1000+ должников)
export interface DebtorsStats {
  total_debt: number;      // Сумма всех отрицательных балансов (в абсолютном значении)
  average_debt: number;    // Среднее значение долга
  total_count: number;     // Количество должников
  critical_count: number;  // Количество должников с долгом > 5000₽
}

export interface DebtorsStatsParams {
  date_from?: string;
  date_to?: string;
  min_debt?: number;
  [key: string]: unknown; // Для дополнительных параметров
}

export async function getDebtorsStats(
  params: DebtorsStatsParams = {}
): Promise<DebtorsStats> {
  const response = await apiClient.get('/customers/debtors/stats', { params });
  return response.data;
}

// Customer Statuses API
export interface CustomerStatus {
  id: number;
  name: string;
  is_system?: boolean;
  image_url?: string;
}

export interface CustomerStatusListResponse {
  statuses: CustomerStatus[];
}

export async function getCustomerStatuses(): Promise<CustomerStatusListResponse> {
  const response = await apiClient.get<CustomerStatusListResponse>('/customer-statuses');
  return response.data;
}

export async function createCustomerStatus(data: { name: string }): Promise<CustomerStatus> {
  const response = await apiClient.post<CustomerStatus>('/customer-statuses', data);
  return response.data;
}

export async function updateCustomerStatus(
  id: number,
  data: { name: string }
): Promise<CustomerStatus> {
  const response = await apiClient.put<CustomerStatus>(`/customer-statuses/${id}`, data);
  return response.data;
}

export async function deleteCustomerStatus(id: number): Promise<void> {
  await apiClient.delete(`/customer-statuses/${id}`);
}


// Печать бланка по абоненту (эталон — CustomerHtml::renderDocsCard:2234, DocumentAR::getBodyDocument).

export interface CustomerDocumentTemplate {
  id: number;
  name: string;
}

export async function getCustomerDocumentTemplates(
  customerId: number
): Promise<{ items: CustomerDocumentTemplate[] }> {
  const response = await apiClient.get<{ items: CustomerDocumentTemplate[] }>(
    `/customers/${customerId}/document-templates`
  );
  return response.data;
}

/**
 * Ответ — JSON с готовым HTML, а не страница по ссылке: токен лежит в localStorage,
 * и GET в новой вкладке ушёл бы без заголовка Authorization.
 */
export async function renderCustomerDocument(
  customerId: number,
  templateId: number
): Promise<{ html: string }> {
  const response = await apiClient.get<{ html: string }>(
    `/customers/${customerId}/document-templates/${templateId}/render`
  );
  return response.data;
}

export const customersApi = {
  getCustomers,
  getCustomerCreateDefaults,
  getCustomer,
  getCustomerStats,
  exportCustomers,
  createCustomer,
  updateCustomer,
  deleteCustomer,
  checkCustomerDeleteBlockers,
  changeBalance,
  getBalanceHistory,
  getCustomerDevices,
  getServices,
  getTariffs,
  getCustomerStatuses,
  createCustomerStatus,
  updateCustomerStatus,
  deleteCustomerStatus,
  getCustomerServices,
  getCustomerPhones,
  createCustomerPhone,
  updateCustomerPhone,
  deleteCustomerPhone,
  getCustomerGroups,
  createCustomerGroup,
  updateCustomerGroup,
  deleteCustomerGroup,
  getCustomerIPs,
  createCustomerIP,
  updateCustomerIP,
  deleteCustomerIP,
  getCustomerTransactionsPage,
  getCustomerChangesHistory,
  getCustomerInventoryMovements,
  getCustomerCommutationHistory,
  getCustomerDirectCommutations,
  getCustomerHistoryFeed,
  changeTariff,
  changeStatus,
  changePassword,
  getCustomerMessages,
  getAllTags,
  getCustomerAvailableTags,
  getCustomerTags,
  addCustomerTag,
  removeCustomerTag,
  getCustomerAdvertisements,
  getAvailableAdvertisementsForCustomer,
  attachCustomerAdvertisement,
  detachCustomerAdvertisement,
  getCustomerOnuDevices,
  getCustomerMacHistory,
  getCustomersActivity,
  getActivitySources,
  getActivityChart,
  getGrowthReport,
  getDebtorsStats,
  getCustomerDocumentTemplates,
  renderCustomerDocument,
};

/**
 * Вложения сообщения абонента.
 *
 * Форма снята с самого типа сервера (`internal/common/attachments/types.go:119`), а не
 * угадана по имени: поля **camelCase** — `mimeType`, `uploadedAt`, `uploadedBy`. Поле
 * `storagePath` НЕ приходит намеренно (`json:"-"`): оно раскрывало бы оператору
 * файловую раскладку сервера.
 *
 * Эталон: `MessageHtml.php:354-357` — ответ ОПЕРАТОРА вешает файл на уже существующее
 * сообщение (`TYPE_MESSAGE_ANSWER`, `objectId1 = msg_id`), то есть однофазно, ровно как
 * наши три роута. Двухфазная форма (`TEMP`-тип, файл до создания сообщения) — это
 * composer нового обращения, отдельный слайс.
 */
export interface MessageAttachment {
  uuid: string;
  filename: string;
  mimeType?: string;
  size?: number;
  uploadedAt?: string;
  uploadedBy?: number;
  comment?: string;
}

export async function listMessageAttachments(
  customerId: number,
  messageId: number,
): Promise<MessageAttachment[]> {
  const { data } = await apiClient.get<{ items: MessageAttachment[]; total: number }>(
    `/customers/${customerId}/messages/${messageId}/attachments`,
  );
  // `toArray` вместо `data.items ?? []`: дефолт не покрывает `null` с провода, а
  // следующий `.map` роняет вкладку целиком (см. shared/lib/toArray.ts).
  return toArray<MessageAttachment>(data?.items);
}

export async function uploadMessageAttachment(
  customerId: number,
  messageId: number,
  file: File,
  comment?: string,
): Promise<MessageAttachment> {
  const form = new FormData();
  form.append('file', file);
  if (comment?.trim()) form.append('comment', comment.trim());
  const { data } = await apiClient.post<{ item: MessageAttachment }>(
    `/customers/${customerId}/messages/${messageId}/attachments`,
    form,
  );
  return data.item;
}

export async function deleteMessageAttachment(
  customerId: number,
  messageId: number,
  uuid: string,
): Promise<void> {
  await apiClient.delete(`/customers/${customerId}/messages/${messageId}/attachments/${uuid}`);
}

/** Умолчания формы создания абонента (эталон: CustomerHtml.php:602-609). */
export interface CustomerCreateDefaults {
  /** Предвыбранный тариф; null, если настройка пуста или тариф не найден. */
  tariff_id: number | null;
  /** Статус, который получит новый абонент. Показывается, но НЕ отправляется: его ставит сервер. */
  status_id: number;
}

/**
 * Умолчания идут отдельной ручкой под правом СОЗДАНИЯ, а не из /settings/customers:
 * та закрыта правом system.settings, которого у оператора создания может не быть.
 * Сервер отдаёт уже разрешённое значение (id тарифа), а не ключ настройки.
 */
export async function getCustomerCreateDefaults(): Promise<CustomerCreateDefaults> {
  const response = await apiClient.get<CustomerCreateDefaults>('/customers/create-defaults');
  return response.data;
}
